import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * CommitteeTimeline - Visualizes committee service over time
 * Each committee type gets its own row to avoid overlapping
 *
 * Props:
 * - committeeRecords: Array of committee service records with DateStart1, DateEnd1
 * - overallStart: Earliest date of service
 * - overallEnd: Latest date of service (or current date if still serving)
 */
export default function CommitteeTimeline({
  committeeRecords = [],
  overallStart,
  overallEnd
}) {
  // Colors for different committee types
  const COLORS = {
    'House Standing': '#2e7d32',
    'House Select': '#4caf50',
    'Senate Standing': '#c62828',
    'Senate Select': '#ef5350',
    'Senate Legislation': '#d32f2f',
    'Senate References': '#e53935',
    'Joint Standing': '#1565c0',
    'Joint Select': '#1976d2',
    'Joint Statutory': '#1e88e5',
    'default': '#757575'
  };

  // Background colors for rows (lighter versions)
  const ROW_BACKGROUNDS = {
    'House Standing': '#e8f5e9',
    'House Select': '#e8f5e9',
    'Senate Standing': '#ffebee',
    'Senate Select': '#ffebee',
    'Senate Legislation': '#ffebee',
    'Senate References': '#ffebee',
    'Joint Standing': '#e3f2fd',
    'Joint Select': '#e3f2fd',
    'Joint Statutory': '#e3f2fd',
    'default': '#f5f5f5'
  };

  // Parse date to timestamp
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).getTime();
  };

  // Get current date for ongoing service
  const now = new Date().getTime();

  // Determine timeline bounds from committee records
  let timelineStart = parseDate(overallStart);
  let timelineEnd = parseDate(overallEnd) || now;

  // Calculate from records if not provided
  if (!timelineStart || !timelineEnd) {
    committeeRecords.forEach(record => {
      const start = parseDate(record.DateStart1);
      const end = parseDate(record.DateEnd1) || now;
      if (start && (!timelineStart || start < timelineStart)) {
        timelineStart = start;
      }
      if (end && end > timelineEnd) {
        timelineEnd = end;
      }
    });
  }

  const totalDuration = timelineEnd - timelineStart;

  if (totalDuration <= 0 || committeeRecords.length === 0) return null;

  // Build committee periods
  const committeePeriods = committeeRecords
    .map(record => ({
      start: parseDate(record.DateStart1),
      end: parseDate(record.DateEnd1) || now,
      type: record.Value1 || 'Committee',
      name: record.Value2 || record.Value1,
      role: record.Value3
    }))
    .filter(p => p.start)
    .sort((a, b) => a.start - b.start);

  // Group periods by committee type
  const periodsByType = committeePeriods.reduce((acc, period) => {
    if (!acc[period.type]) acc[period.type] = [];
    acc[period.type].push(period);
    return acc;
  }, {});

  // Sort types for consistent ordering (House, Senate, Joint, Other)
  const typeOrder = ['House Standing', 'House Select', 'Senate Standing', 'Senate Select',
                     'Senate Legislation', 'Senate References', 'Joint Standing',
                     'Joint Select', 'Joint Statutory'];

  const sortedTypes = Object.keys(periodsByType).sort((a, b) => {
    const aIndex = typeOrder.indexOf(a);
    const bIndex = typeOrder.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Calculate position as percentage
  const getPosition = (timestamp) => {
    return ((timestamp - timelineStart) / totalDuration) * 100;
  };

  // Format date for display
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-AU', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Generate year markers
  const startYear = new Date(timelineStart).getFullYear();
  const endYear = new Date(timelineEnd).getFullYear();
  const yearMarkers = [];

  // Determine step size based on duration
  const yearSpan = endYear - startYear;
  const step = yearSpan > 40 ? 10 : yearSpan > 20 ? 5 : yearSpan > 10 ? 2 : 1;

  for (let year = Math.ceil(startYear / step) * step; year <= endYear; year += step) {
    const timestamp = new Date(year, 0, 1).getTime();
    if (timestamp >= timelineStart && timestamp <= timelineEnd) {
      yearMarkers.push({ year, position: getPosition(timestamp) });
    }
  }

  // Get color for committee type
  const getColor = (type) => {
    // Try exact match first
    if (COLORS[type]) return COLORS[type];

    // Try partial match
    if (type.includes('House') && type.includes('Standing')) return COLORS['House Standing'];
    if (type.includes('House') && type.includes('Select')) return COLORS['House Select'];
    if (type.includes('Senate') && type.includes('Standing')) return COLORS['Senate Standing'];
    if (type.includes('Senate') && type.includes('Select')) return COLORS['Senate Select'];
    if (type.includes('Senate') && type.includes('Legislation')) return COLORS['Senate Legislation'];
    if (type.includes('Senate') && type.includes('References')) return COLORS['Senate References'];
    if (type.includes('Joint') && type.includes('Standing')) return COLORS['Joint Standing'];
    if (type.includes('Joint') && type.includes('Select')) return COLORS['Joint Select'];
    if (type.includes('Joint') && type.includes('Statutory')) return COLORS['Joint Statutory'];

    return COLORS['default'];
  };

  // Get background color for row
  const getRowBackground = (type) => {
    if (ROW_BACKGROUNDS[type]) return ROW_BACKGROUNDS[type];

    if (type.includes('House')) return ROW_BACKGROUNDS['House Standing'];
    if (type.includes('Senate')) return ROW_BACKGROUNDS['Senate Standing'];
    if (type.includes('Joint')) return ROW_BACKGROUNDS['Joint Standing'];

    return ROW_BACKGROUNDS['default'];
  };

  // Render a period bar
  const renderPeriod = (period, index) => {
    const left = getPosition(period.start);
    const width = getPosition(period.end) - left;
    const color = getColor(period.type);

    return (
      <Tooltip
        key={`committee-${period.type}-${index}`}
        title={
          <Box sx={{ color: '#fff' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
              {period.name}
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: 'inherit' }}>
              {period.type}
            </Typography>
            {period.role && (
              <Typography variant="caption" display="block" sx={{ color: 'inherit' }}>
                Role: {period.role}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9, color: 'inherit' }}>
              {formatDate(period.start)} — {period.end >= now - 86400000 ? 'Present' : formatDate(period.end)}
            </Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          component={RouterLink}
          to={`/committees?q=CommitteeName:"${encodeURIComponent(period.name)}"`}
          sx={{
            position: 'absolute',
            left: `${left}%`,
            width: `${Math.max(width, 0.5)}%`,
            height: '100%',
            bgcolor: color,
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            textDecoration: 'none',
            '&:hover': {
              transform: 'scaleY(1.3)',
              zIndex: 10,
              boxShadow: `0 2px 8px ${color}88`
            }
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {sortedTypes.map(type => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: getColor(type), borderRadius: '2px' }} />
            <Typography variant="caption" color="text.secondary">
              {type} ({periodsByType[type].length})
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Timeline container */}
      <Box sx={{
        position: 'relative',
        bgcolor: '#f5f5f5',
        borderRadius: 2,
        p: 2,
        pt: 1
      }}>
        {/* Year markers */}
        <Box sx={{ position: 'relative', height: 20, mb: 1 }}>
          {yearMarkers.map(({ year, position }) => (
            <Typography
              key={year}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${position}%`,
                transform: 'translateX(-50%)',
                color: 'text.secondary',
                fontSize: '0.7rem'
              }}
            >
              {year}
            </Typography>
          ))}
        </Box>

        {/* Committee service rows - one per type */}
        {sortedTypes.map((type, typeIndex) => (
          <Box key={type} sx={{ mb: typeIndex < sortedTypes.length - 1 ? 1 : 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mb: 0.5,
                display: 'block',
                fontSize: '0.7rem',
                fontWeight: 500
              }}
            >
              {type}
            </Typography>
            <Box sx={{
              position: 'relative',
              height: 20,
              bgcolor: getRowBackground(type),
              borderRadius: '4px',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              {periodsByType[type].map((period, i) => renderPeriod(period, i))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
